import React, { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import * as S from "./styles";
import api from "../../../services/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { TaglineG, TaglineP } from "../../../styles/stylesTypography";
import { theme } from "../../../styles/theme";

import IconSearchNotFound from "../../../assets/icons/icons8-search-notfound.png";
import FunctionBlockedOverlay from "../../functionBlocked";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const isFunctionBlocked = (user) => {
  const planId = user?.contrato?.agro_plan_config?.agro_plan_id;
  const diasRestante = Number(user?.contrato?.dias_restante ?? 0);
  return (
    planId === 7 ||
    planId === 6 ||
    planId === 4 ||
    (planId === 1 && diasRestante <= 0)
  );
};

const getUserFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem("@userinfo"));
  } catch {
    return null;
  }
};

const QuotationGraphic = ({ farmId, blockedGroupRef, overlayMode = "self" }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [selectedCultureType, setSelectedCultureType] = useState("");
  const [cultureTypes, setCultureTypes] = useState([]);
  const [lastValue, setLastValue] = useState(0);
  const [percentageChange, setPercentageChange] = useState(0);
  const [filteredSortedData, setFilteredSortedData] = useState([]);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState('')
  const blockedRef = useRef(null);
  const isBlocked = isFunctionBlocked(getUserFromStorage());
  const overlayTargetRef = blockedGroupRef ?? blockedRef;

  const CultureQuotation = async (fazendaId) => {
    try {
      const response = await api.get(`/culturas/cotacoes/${fazendaId}`);

      if (response.data.length > 0) {
        const processedData = response.data.map(item => ({
          valor: parseFloat(item.valor.replace(".", "").replace(",", ".")),
          tipo_cultura: item.tipo_cultura,
          data: new Date(item.data),
        }));
  
        setData(processedData);
  
        const ultimoItem = response.data.at(-1);
        setUltimaAtualizacao(new Date(ultimoItem.created))
  
        const tipos = [...new Set(processedData.map(item => item.tipo_cultura))];
        setCultureTypes(tipos);
  
        if (tipos.length > 0) {
          setSelectedCultureType(tipos[0]);
          // prepareChartData(processedData, tipos[0]);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Erro ao fazer a requisição:', error);
      setError(error);
      setLoading(false);
    }
  };

  const filterLast11Months = (data) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    let startYear = currentYear;
    let startMonth = currentMonth - 11;

    if (startMonth < 1) {
      startMonth += 12;
      startYear -= 1;
    }

    const filteredData = data.filter(item => {
      const itemYear = item.data.getFullYear();
      const itemMonth = item.data.getMonth() + 1;

      if (itemYear > startYear || (itemYear === startYear && itemMonth >= startMonth)) {
        return true;
      }
      return false;
    });

    return filteredData;
  };

  const prepareChartData = (data, tipoCultura) => {
    const filteredData = data.filter(item => item.tipo_cultura === tipoCultura);

    const last11MonthsData = filterLast11Months(filteredData);

    const sortedData = last11MonthsData.sort((a, b) => a.data - b.data);
    setFilteredSortedData(sortedData);
    let computedPercentage = 0;

    if (sortedData.length > 0) {
      const last = sortedData[sortedData.length - 1].valor;
      const previous = sortedData[sortedData.length - sortedData.length]?.valor || last;
      computedPercentage = previous === 0 ? 0 : ((last - previous) / previous) * 100;

      setLastValue(last);
      setPercentageChange(computedPercentage);
    }

    const isNegative = computedPercentage < 0;

    const lineColor = isNegative ? "#FF5252" : "#2FBB69";
    const gradientColor = isNegative ? "rgba(255, 82, 82, 0.4)" : "rgba(47, 187, 105, 0.4)";

    setChartData({
      labels: sortedData.map(item => formatDate(item.data)),
      datasets: [
        {
          label: "Valor",
          data: sortedData.map(item => item.valor),
          borderColor: lineColor,
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              return null;
            }

            const gradient = ctx.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom
            );
            gradient.addColorStop(0, gradientColor);
            gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

            return gradient;
          },
          tension: 0.4,
          fill: true,
        },
      ],
    });
  };

  const formatDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Mês começa de 0 (janeiro = 0), então adicionamos 1
    const year = date.getFullYear();
    return `01/${month}/${year}`; // Retorna no formato "01/MM/AAAA"
  };

  const formatDateMY = (date) => {
    if (date) {
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Mês começa de 0 (janeiro = 0), então adicionamos 1
      const year = date.getFullYear();
      return `${month}/${year}`; // Retorna no formato "MM/AAAA"
    }
  };

  useEffect(() => {
    CultureQuotation(farmId);
  }, [farmId]);

  useEffect(() => {
    if (selectedCultureType) {
      prepareChartData(data, selectedCultureType);
    }
  }, [selectedCultureType, data]);

  useEffect(() => {
    if (cultureTypes.length > 1) {
      setSelectedCultureType(cultureTypes[1]);
    }
  }, [cultureTypes]);

  if (loading) {
    return <></>;
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => {
            return ` Valor: R$ ${context.raw.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
        type: "linear",
        ticks: {
          callback: (value) =>
            `R$ ${value.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
        },
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
  };

  const getCurrentMonthYear = () => {
    const currentDate = new Date();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // Mês começa de 0 (janeiro = 0), então adicionamos 1
    const year = currentDate.getFullYear();
    return `${month}/${year}`; // Retorna no formato "MM/AAAA"
  };

  return (
    <S.Container ref={blockedRef}>
      {data.length !== 0 ? (
        <>
          <div className="rate-info">
            <TaglineP color={theme.colors.gray.Cinza_2} fontweight={600}>Atualizado Em {formatDateMY(ultimaAtualizacao)}</TaglineP>

            <div className="custom-selector">
              <select
                value={selectedCultureType}
                onChange={(e) => setSelectedCultureType(e.target.value)}
              >
                {cultureTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <p style={{ fontSize: "20px", fontWeight: 800, color: "#21262F" }}>
              R${" "}
              {lastValue.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>{" "}
          </div>

          <div
            style={{
              color: percentageChange >= 0 ? `${theme.colors.green.Verde_AgroReceita}` : `${theme.colors.red.Vermelho_erro}`,
              fontSize: "11px",
              textAlign: "center",
            }}
          >
            {percentageChange >= 0 ? "+" : ""}
            {percentageChange.toFixed(2)}% <br />Últimos 12 meses
          </div>

          <p style={{ fontSize: "10px" }}>Valor <a href={`https://www.agrolink.com.br/cotacoes/`} target="_blank" rel="noopener noreferrer">Nacional</a></p>

          <div className="chart-wrapper">
            <Line data={chartData} options={options} />
          </div>
        </>
        ):(
        <S.ContainerContent>
          <img src={IconSearchNotFound} alt="Ícone de pesquisa" />

          <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={800}>
            Nenhuma cultura encontrada para <br/>
            esse produtor
          </TaglineP>
        </S.ContainerContent>
      )}

      {overlayMode !== "none" && isBlocked && (
        <FunctionBlockedOverlay
          visible
          mode="cover-parent"
          attachTo={overlayTargetRef}
          onActivate={() => {}}
        />
      )}
    </S.Container>
  );
};

export default QuotationGraphic;
